import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import moment from "moment";
import { AdminTabs } from "../Admin";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import { postData, deleteData } from "../../../components/utilities/UtilityFunctions";
import GameSystemPicker from "../../../components/utilities/gamesystempickers/GameSystemPicker";
import GenericPicker from "../../../components/utilities/GenericPicker";
import DatePickerComponent from "../../../components/utilities/DatePickerComponent";
import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import {
  BCPFloppyDisk,
  BCPSquarePlus,
  BCPAngleUp,
  BCPAngleDown
} from "../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const SINGLE_GAME_MANUFACTURER = getEnvironmentVariable(
  "SINGLE_GAME_MANUFACTURER"
);


const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return moment(dateStr).format("DD MMM YYYY");
};

const SortableHeader = ({ field, label, sortField, sortDir, onSort }) => (
  <Box
    onClick={() => onSort(field)}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      cursor: "pointer",
      userSelect: "none",
      "&:hover": { opacity: 0.75 }
    }}
  >
    {label}
    {sortField === field &&
      (sortDir === "asc" ? (
        <BCPAngleUp size="xs" />
      ) : (
        <BCPAngleDown size="xs" />
      ))}
  </Box>
);

export default function AdminMilestones(props) {
  const { activeTab, handleTabChange, authContext } = props;

  const [loading, setLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [gameSystems, setGameSystems] = useState([]);
  const [gameSystem, setGameSystem] = useState(null);
  const [milestones, setMilestones] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [filterActive, setFilterActive] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({});

  const [sortField, setSortField] = useState("milestoneDate");
  const [sortDir, setSortDir] = useState("desc");

  const [openCreate, setOpenCreate] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    const go = async () => {
      let queryParams = {};
      if (SINGLE_GAME_MANUFACTURER) {
        queryParams.manufacturer = SINGLE_GAME_MANUFACTURER;
      }
      let apiGameSystems = await handlePagingAll("/gamesystems", queryParams);
      if (
        !authContext?.authState?.identity?.isAdmin &&
        authContext?.authState?.identity?.adminGameSystems?.length > 0
      ) {
        apiGameSystems = apiGameSystems.filter((gs) =>
          authContext.authState.identity.adminGameSystems.includes(gs.id)
        );
      }
      setGameSystems(apiGameSystems || []);
      setLoading(false);
    };
    go(); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const go = async () => {
      setSubLoading(true);
      setMilestones([]);
      setEditingId(null);
      setEditingValues({});
      setSearchText("");
      setFilterActive("");
      setSortField("milestoneDate");
      setSortDir("desc");

      if (gameSystem) {
        const data = await handlePagingAll("/milestones", {
          gameSystemId: gameSystem.id
        });
        setMilestones(data || []);
      }
      setSubLoading(false);
    };
    go();
  }, [gameSystem]);

  const startEditing = (milestone) => {
    setEditingId(milestone.id);
    setEditingValues({
      name: milestone.name,
      description: milestone.description || "",
      milestoneDate: milestone.milestoneDate,
      milestoneEndDate: milestone.milestoneEndDate,
      active: milestone.active
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingValues({});
  };

  const saveEditing = async () => {
    setProcessing(true);
    const updated = await postData(`/milestones/${editingId}`, {
      body: { set: editingValues }
    });
    if (updated) {
      setMilestones((prev) =>
        prev.map((m) => (m.id === editingId ? { ...m, ...updated } : m))
      );
      postNotification("success", "Milestone updated");
      setEditingId(null);
      setEditingValues({});
    }
    setProcessing(false);
  };

  const handleCreateMilestone = async (values, { resetForm }) => {
    setProcessing(true);
    const created = await postData("/milestones", {
      body: { ...values, gameSystemId: gameSystem.id }
    });
    if (created) {
      setMilestones((prev) => [created, ...prev]);
      postNotification("success", "Milestone created");
      setOpenCreate(false);
      resetForm();
    }
    setProcessing(false);
  };

  const handleDeleteMilestone = async () => {
    if (!deleteConfirmId) return;
    setProcessing(true);
    await deleteData(`/milestones/${deleteConfirmId}`);
    setMilestones((prev) => prev.filter((m) => m.id !== deleteConfirmId));
    postNotification("success", "Milestone deleted");
    setDeleteConfirmId(null);
    setEditingId(null);
    setEditingValues({});
    setProcessing(false);
  };

  const hasActiveFilters = Boolean(searchText || filterActive);

  const hasNonDefaultSort = sortField !== "milestoneDate" || sortDir !== "desc";

  const handleClearFilters = () => {
    setSearchText("");
    setFilterActive("");
  };

  const handleClearSort = () => {
    setSortField("milestoneDate");
    setSortDir("desc");
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const filteredMilestones = milestones.filter((m) => {
    if (
      searchText &&
      !m.name.toLowerCase().includes(searchText.toLowerCase())
    )
      return false;
    if (filterActive === "true" && !m.active) return false;
    if (filterActive === "false" && m.active) return false;
    return true;
  });

  const sortedMilestones = [...filteredMilestones].sort((a, b) => {
    if (sortField === "name") {
      const cmp = (a.name || "").localeCompare(b.name || "");
      return sortDir === "asc" ? cmp : -cmp;
    }
    if (sortField === "active") {
      const cmp = a.active === b.active ? 0 : a.active ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    }
    const aDate = a[sortField] ? new Date(a[sortField]) : new Date(0);
    const bDate = b[sortField] ? new Date(b[sortField]) : new Date(0);
    return sortDir === "asc" ? aDate - bDate : bDate - aDate;
  });

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary"
          }}
        >
          <Typography variant="h3">Admin Settings</Typography>
          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>

        {gameSystems.length > 0 && (
          <Grid
            item
            xs={12}
            sx={{
              p: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              borderRadius: 1,
              boxShadow: 1,
              backgroundColor: "background.secondary"
            }}
          >
            <GameSystemPicker
              name="gameSystem"
              value={gameSystem?.id || ""}
              valueType="id"
              handleChange={(e) => {
                if (e.target.value) {
                  setGameSystem(
                    gameSystems.find((gs) => gs.id === e.target.value) || null
                  );
                } else {
                  setGameSystem(null);
                }
              }}
              gameSystems={gameSystems}
            />
          </Grid>
        )}

        {gameSystem && (
          <>
            <Grid
              item
              xs={12}
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "background.secondary"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em"
                  }}
                >
                  Filters
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<BCPSquarePlus />}
                  onClick={() => setOpenCreate(true)}
                >
                  New Milestone
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={9}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <GenericPicker
                    name="filterActive"
                    label="Status"
                    value={filterActive}
                    handleChange={(e) => setFilterActive(e.target.value)}
                    list={[
                      { label: "Active", value: "true" },
                      { label: "Inactive", value: "false" }
                    ]}
                  />
                </Grid>
                {hasActiveFilters && (
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClearFilters}
                    >
                      Clear Filters
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 0.5
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {subLoading
                      ? "Loading milestones..."
                      : `Showing ${sortedMilestones.length} milestone${sortedMilestones.length !== 1 ? "s" : ""}`}
                  </Typography>
                  {hasNonDefaultSort && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearSort}
                      sx={{ fontSize: "0.7rem", py: 0, minWidth: 0 }}
                    >
                      Clear Sort
                    </Button>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  Click any row to edit
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "background.secondary",
                overflow: "hidden"
              }}
            >
              <TableContainer
                component={Paper}
                sx={{ backgroundColor: "background.secondary" }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "18%" }}>
                        <SortableHeader field="name" label="Name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "22%" }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "16%" }}>
                        <SortableHeader field="milestoneDate" label="Start Date" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "16%" }}>
                        <SortableHeader field="milestoneEndDate" label="End Date" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "10%" }}>
                        <SortableHeader field="active" label="Active" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      </TableCell>
                      <TableCell sx={{ width: "18%" }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subLoading && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                            <CircularProgress />
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {!subLoading && sortedMilestones.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 3 }}
                          >
                            No milestones found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {sortedMilestones.map((milestone) => {
                      const isEditing = editingId === milestone.id;
                      return (
                        <TableRow
                          key={milestone.id}
                          hover={!isEditing}
                          onClick={
                            !isEditing ? () => startEditing(milestone) : undefined
                          }
                          sx={{ cursor: !isEditing ? "pointer" : "default" }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                fullWidth
                                value={editingValues.name}
                                onChange={(e) =>
                                  setEditingValues((prev) => ({
                                    ...prev,
                                    name: e.target.value
                                  }))
                                }
                              />
                            ) : (
                              <Typography variant="body2">
                                {milestone.name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                value={editingValues.description}
                                onChange={(e) =>
                                  setEditingValues((prev) => ({
                                    ...prev,
                                    description: e.target.value
                                  }))
                                }
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 180
                                }}
                              >
                                {milestone.description || "—"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 200, py: 2 }}>
                            {isEditing ? (
                              <DatePickerComponent
                                name="milestoneDate"
                                label="Start Date"
                                value={editingValues.milestoneDate}
                                setFieldValue={(name, val) =>
                                  setEditingValues((prev) => ({
                                    ...prev,
                                    milestoneDate: val
                                  }))
                                }
                                statsNullable
                                margin="dense"
                              />
                            ) : (
                              <Typography variant="body2">
                                {formatDate(milestone.milestoneDate)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 200, py: 2 }}>
                            {isEditing ? (
                              <DatePickerComponent
                                name="milestoneEndDate"
                                label="End Date"
                                value={editingValues.milestoneEndDate}
                                setFieldValue={(name, val) =>
                                  setEditingValues((prev) => ({
                                    ...prev,
                                    milestoneEndDate: val
                                  }))
                                }
                                statsNullable
                                margin="dense"
                              />
                            ) : (
                              <Typography variant="body2">
                                {formatDate(milestone.milestoneEndDate)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ py: 2 }}
                            onClick={(e) => { if (!isEditing) startEditing(milestone); else e.stopPropagation(); }}
                          >
                            <Switch
                              size="small"
                              checked={
                                isEditing ? editingValues.active : milestone.active
                              }
                              onChange={(e) => {
                                if (editingId && editingId !== milestone.id) return;
                                if (!isEditing) {
                                  setEditingId(milestone.id);
                                  setEditingValues({
                                    name: milestone.name,
                                    description: milestone.description || "",
                                    milestoneDate: milestone.milestoneDate,
                                    milestoneEndDate: milestone.milestoneEndDate,
                                    active: e.target.checked
                                  });
                                } else {
                                  setEditingValues((prev) => ({
                                    ...prev,
                                    active: e.target.checked
                                  }));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{ py: 2 }}
                            onClick={(e) => { if (!isEditing) startEditing(milestone); else e.stopPropagation(); }}
                          >
                            {isEditing && (
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={cancelEditing}
                                  sx={{ px: 0.75, fontSize: "0.68rem", whiteSpace: "nowrap", minWidth: 0 }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<BCPFloppyDisk />}
                                  onClick={saveEditing}
                                  disabled={processing}
                                  sx={{ px: 0.75, fontSize: "0.68rem", whiteSpace: "nowrap", minWidth: 0 }}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => setDeleteConfirmId(milestone.id)}
                                  disabled={processing}
                                  sx={{ px: 0.75, fontSize: "0.68rem", whiteSpace: "nowrap", minWidth: 0 }}
                                >
                                  Delete
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </>
        )}
      </Grid>

      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New Milestone</DialogTitle>
        <Formik
          initialValues={{
            name: "",
            description: "",
            milestoneDate: null,
            milestoneEndDate: null,
            active: true
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().required("Name is required"),
            milestoneDate: Yup.mixed().nullable().required("Start date is required")
          })}
          onSubmit={handleCreateMilestone}
        >
          {({ handleChange, handleSubmit, values, errors, touched, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Game System"
                      value={gameSystem?.name || ""}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      multiline
                      rows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePickerComponent
                      name="milestoneDate"
                      label="Start Date"
                      value={values.milestoneDate}
                      setFieldValue={setFieldValue}
                      statsNullable
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DatePickerComponent
                      name="milestoneEndDate"
                      label="End Date"
                      value={values.milestoneEndDate}
                      setFieldValue={setFieldValue}
                      statsNullable
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Active"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)"
                        }
                      }}
                      control={
                        <Switch
                          checked={values.active}
                          onChange={() =>
                            setFieldValue("active", !values.active)
                          }
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<BCPFloppyDisk />}
                  disabled={processing}
                >
                  Create
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Milestone</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this milestone? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteMilestone}
            disabled={processing}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {Boolean(processing || loading) && <ModalLoadingFull />}
    </Container>
  );
}
